import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { formatDate } from "../../utils";
import { formatAxiosErrorMessage, postOrThrow } from "../../_api";
import {
  Alert,
  Button,
  Flex,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";

export interface AddProps {
  updateVisits(): void;
}

const DEFAULT_AMOUNT = 500;
const DEFAULT_HOUR = "08";
const DEFAULT_MINUTES = "00";

/** Local calendar day + slot time, always full hour (ms cleared). */
function visitAtSlot(day: Date, hour: string, minutes: string): Date {
  const d = new Date(day);
  d.setHours(parseInt(hour, 10), parseInt(minutes, 10), 0, 0);
  return d;
}

function defaultVisitDate(): Date {
  return visitAtSlot(new Date(), DEFAULT_HOUR, DEFAULT_MINUTES);
}

const HOUR_OPTIONS = [
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
];

const ALERT_STICKY = {
  mb: "md" as const,
  style: { position: "sticky" as const, top: 0, zIndex: 200 },
};

const Add: React.FC<AddProps> = ({ updateVisits }) => {
  const [hour, setHour] = useState<string>(DEFAULT_HOUR);
  const [minutes, setMinutes] = useState<string>(DEFAULT_MINUTES);
  const [info, setInfo] = useState<string>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const infoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (infoTimeoutRef.current != null) {
        clearTimeout(infoTimeoutRef.current);
      }
    };
  }, []);

  const dismissInfo = () => {
    if (infoTimeoutRef.current != null) {
      clearTimeout(infoTimeoutRef.current);
      infoTimeoutRef.current = null;
    }
    setInfo(undefined);
  };

  return (
    <Stack gap="md" py="md">
      {submitError ? (
        <Alert
          color="red"
          variant="filled"
          title="Nie zapisano wizyty"
          withCloseButton
          onClose={() => setSubmitError(null)}
          {...ALERT_STICKY}
        >
          <Text size="sm" c="white" style={{ whiteSpace: "pre-wrap" }}>
            {submitError}
          </Text>
        </Alert>
      ) : info ? (
        <Alert
          color="green"
          variant="filled"
          title="Sukces"
          withCloseButton
          onClose={dismissInfo}
          {...ALERT_STICKY}
        >
          <Text size="sm" c="white" style={{ whiteSpace: "pre-wrap" }}>
            {info}
          </Text>
        </Alert>
      ) : null}
      <Formik
        initialValues={{ date: defaultVisitDate(), amount: DEFAULT_AMOUNT }}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitError(null);
          try {
            const date = visitAtSlot(
              new Date(values.date),
              hour,
              minutes
            );
            await postOrThrow("visits/save", {
              values: { ...values, date },
            });
            updateVisits();
            setInfo("Dodano nową wizytę!");
            if (infoTimeoutRef.current != null) {
              clearTimeout(infoTimeoutRef.current);
            }
            infoTimeoutRef.current = setTimeout(() => {
              infoTimeoutRef.current = null;
              setInfo(undefined);
            }, 1000);
          } catch (e) {
            setSubmitError(formatAxiosErrorMessage(e));
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, setFieldValue, values }) => (
          <form onSubmit={handleSubmit}>
            <Stack gap="lg">
              <Flex
                gap="xl"
                align="flex-start"
                justify="flex-start"
                direction={{ base: "column", md: "row" }}
                wrap="wrap"
              >
                <Stack gap="sm" flex={1} miw={{ base: "100%", md: 260 }} maw={{ md: 380 }}>
                  <Title order={4}>Wybierz dzień</Title>
                  <Calendar
                    selectRange={false}
                    value={values.date}
                    onChange={(value: Date | Date[]) => {
                      const raw = Array.isArray(value) ? value[0] : value;
                      setFieldValue(
                        "date",
                        visitAtSlot(new Date(raw), hour, minutes)
                      );
                    }}
                  />
                </Stack>
                <Stack gap="sm" flex={1} miw={{ base: "100%", md: 200 }} maw={{ md: 320 }}>
                  <Title order={4}>Wybierz godzinę</Title>
                  <NativeSelect
                    data={HOUR_OPTIONS}
                    value={`${hour.padStart(2, "0")}:${minutes.padStart(2, "0")}`}
                    onChange={(e) => {
                      const selectedValues = e.currentTarget.value.split(":");
                      const h = selectedValues[0];
                      const m = selectedValues[1];
                      setHour(h);
                      setMinutes(m);

                      setFieldValue(
                        "date",
                        visitAtSlot(new Date(values.date), h, m)
                      );
                    }}
                  />
                  <Title order={4}>Kwota (PLN)</Title>
                  <NumberInput
                    name="amount"
                    placeholder="500"
                    value={values.amount}
                    onChange={(v) =>
                      setFieldValue(
                        "amount",
                        typeof v === "number" ? v : DEFAULT_AMOUNT
                      )
                    }
                    min={0}
                  />
                </Stack>
                <Stack gap="md" flex={1} miw={{ base: "100%", md: 220 }} maw={{ md: 360 }}>
                  <Title order={4}>Podsumowanie</Title>
                  <Stack gap="xs">
                    {values.date ? (
                      <Text>
                        Wybrany termin:{" "}
                        <Text span fw={700}>
                          {formatDate(new Date(values.date))}
                        </Text>
                      </Text>
                    ) : null}
                    {values.amount != null ? (
                      <Text>
                        Wybrana kwota:{" "}
                        <Text span fw={700}>
                          {values.amount} PLN
                        </Text>
                      </Text>
                    ) : null}
                  </Stack>
                  <Button type="submit" mt="xs">
                    Dodaj
                  </Button>
                </Stack>
              </Flex>
            </Stack>
          </form>
        )}
      </Formik>
    </Stack>
  );
};

export default Add;
